/**
 * Role users functionality - displaying users who have claimed roles
 */

import { apiGet } from '../utils/apiClient.js';
import { t } from '../utils/i18n.js';

type RoleUser = {
    name: string;
    email: string;
    created_at: string;
    last_claimed_at?: string;
};

type RoleUsersResponse = RoleUser[] | { users: RoleUser[] };

/**
 * Load and display users who claimed a specific role
 */
export async function loadRoleUsers(roleId: number): Promise<void> {
    const listDiv = document.getElementById('role-users-list');
    if (!listDiv) return;

    if (!Number.isFinite(roleId)) {
        listDiv.innerHTML = '';
        const p = document.createElement('p');
        p.className = 'panel-empty-state';
        p.textContent = t('panel.people.noClaims');
        listDiv.appendChild(p);
        return;
    }

    try {
        const usersResponse = await apiGet<RoleUsersResponse>(`/api/roles/${roleId}/users`);
        const users = Array.isArray(usersResponse) ? usersResponse : usersResponse?.users || [];

        listDiv.innerHTML = '';
        
        if (users.length === 0) {
            const p = document.createElement('p');
            p.className = 'panel-empty-state';
            p.textContent = t('panel.people.noClaims');
            listDiv.appendChild(p);
        } else {
            users.forEach(user => {
                const firstClaimed = new Date(user.created_at).toLocaleDateString();
                const lastClaimedRaw = user.last_claimed_at;
                const lastClaimed = lastClaimedRaw ? new Date(lastClaimedRaw).toLocaleDateString() : null;
                const dateDisplay = (!lastClaimed || firstClaimed === lastClaimed)
                    ? t('panel.people.since', { date: firstClaimed })
                    : `${t('panel.people.first', { date: firstClaimed })}, ${t('panel.people.current', { date: lastClaimed })}`;
                
                const userItem = document.createElement('div');
                userItem.className = 'role-user-item';
                
                const userName = document.createElement('div');
                userName.className = 'user-name';
                userName.textContent = user.name || user.email;
                
                const claimedDate = document.createElement('div');
                claimedDate.className = 'user-claimed-date';
                claimedDate.textContent = dateDisplay;
                
                userItem.appendChild(userName);
                userItem.appendChild(claimedDate);
                listDiv.appendChild(userItem);
            });
        }
    } catch (error) {
        console.error('Failed to load role users:', error);
        listDiv.innerHTML = '';
        const p = document.createElement('p');
        p.className = 'panel-empty-state';
        p.textContent = 'Error loading users';
        listDiv.appendChild(p);
    }
}
